import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowLeft,
  FaCamera,
  FaUser,
} from "react-icons/fa";
import ReusableHeader from "../../../../reusableField/ReusableHeader";
import { ChevronDown, ChevronUp, DeleteIcon } from "lucide-react";
import { message, Select } from "antd";
import Delete from "../../../../Images/deleteIcon.png";

import {
  createStarPerformer,
  getStarPerformerById,
  updateStarPerformer,
  getStarPerformerLevels,
  getStarPerformerOptions,
  getStarPerformerQuestions,
  addStarPerformerQuestion,
  updateStarPerformerQuestion,
  deleteStarPerformerQuestion,
} from "../../../../services/SyncApis";


let quizIdCounter = 1;

const makeEmptyQuiz = () => ({
  id: quizIdCounter++,
  questionId: null,
  quiz: "",
  questionType: "",
  options: ["", "", "", ""],
  optionIds: [null, null, null, null],
  correctAnswer: "",
  correctAnswers: [],
  isOpen: true,
  isExisting: false,
});

const getQuestionType = (questionType) => {
  const typeId = Number(questionType);

  if (typeId === 1) {
    return "single";
  }

  if (typeId === 2) {
    return "multiple";
  }

  if (typeId === 3) {
    return "trueFalse";
  }

  return "";
};

const SyncAddStar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [videoDuration, setVideoDuration] = useState("");

  const locationState = location.state || {};
  const { isEdit } = locationState;

  const [initialStarData, setInitialStarData] = useState(
    locationState.starData || null
  );

  const starId =
    initialStarData?.id ||
    initialStarData?._id ||
    locationState.starId;

  const { accountId, roleId, changedBy } = locationState;

  const { Option } = Select;

  // =========================================================
  // ROLE JOURNEY LEVELS
  // =========================================================

  const [levels, setLevels] = useState([]);
  const [levelsLoading, setLevelsLoading] = useState(false);

  const [selectedLevelNumber, setSelectedLevelNumber] =
    useState("");

  // =========================================================
  // CONTENTS
  //
  // IMPORTANT:
  // Keep contents in frontend state.
  // Current PATCH API does NOT receive it yet.
  // When backend supports contents in PATCH,
  // we can add it to performerPayload.
  // =========================================================

  const [contents, setContents] = useState([]);

  // =========================================================
  // OPTIONS
  // =========================================================

  const [languages, setLanguages] = useState([]);
  const [videoLanguages, setVideoLanguages] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);

  const [optionsLoading, setOptionsLoading] =
    useState(false);

  // =========================================================
  // ABOUT PERFORMER
  // =========================================================

  const [fullName, setFullName] = useState("");
  const [designation, setDesignation] = useState("");
  const [language, setLanguage] = useState("");

  const [avatar, setAvatar] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  // =========================================================
  // VIDEO DETAILS
  // =========================================================

  const [videoCode, setVideoCode] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [thumbnailCode, setThumbnailCode] =
    useState("");
  const [videoLanguage, setVideoLanguage] =
    useState("");
  const [videoDescription, setVideoDescription] =
    useState("");

  // =========================================================
  // QUIZZES
  // =========================================================

  const [quizzes, setQuizzes] = useState([
    makeEmptyQuiz(),
  ]);


  const [questionsLoading, setQuestionsLoading] =
    useState(false);

  // =========================================================
  // WEIGHTAGE
  // =========================================================

  const [weightage, setWeightage] = useState("");

  // =========================================================
  // ACTIVE / INACTIVE
  // =========================================================

  const [isStarActive, setIsStarActive] =
    useState(true);

  // =========================================================
  // ERRORS
  // =========================================================

  const [errors, setErrors] = useState({
    fullName: "",
    designation: "",
    language: "",
    videoCode: "",
    videoTitle: "",
    thumbnailCode: "",
    videoLanguage: "",
    videoDescription: "",
    videoDuration: "",
    weightage: "",
    level: "",
    quizzes: [],
  });

  // =========================================================
  // LOAD OPTIONS
  // =========================================================

  const loadOptions = async () => {
    setOptionsLoading(true);

    try {
      const response =
        await getStarPerformerOptions();

      const data =
        response?.data || {};

      setLanguages(
        Array.isArray(data.languages)
          ? data.languages
          : []
      );

      setVideoLanguages(
        Array.isArray(data.videoLanguages)
          ? data.videoLanguages
          : []
      );

      setQuestionTypes(
        Array.isArray(data.questionTypes)
          ? data.questionTypes
          : []
      );
    } catch (error) {
      console.error(
        "Failed to load Star Performer options:",
        error
      );

      message.error(
        "Failed to load configuration options."
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  // =========================================================
  // LOAD ROLE JOURNEY LEVELS
  // =========================================================

  const loadLevels = async () => {
    if (!accountId || !roleId) {
      return;
    }

    setLevelsLoading(true);

    try {
      const response =
        await getStarPerformerLevels(
          Number(accountId),
          Number(roleId)
        );

      const data =
        response?.data || [];

      setLevels(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      console.error(
        "Failed to load Role Journey levels:",
        error
      );

      message.error(
        "Failed to load Role Journey levels."
      );
    } finally {
      setLevelsLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
    loadLevels();
  }, [accountId, roleId]);

  // =========================================================
  // LOAD STAR PERFORMER
  // =========================================================

  const fetchStarPerformerById = async (
    id
  ) => {
    try {
      const response =
        await getStarPerformerById(id);

      const performer =
        response?.data ?? response;

      setInitialStarData(
        performer
      );
    } catch (error) {
      console.error(
        "Failed to load star performer:",
        error
      );

      message.error(
        "Failed to load star performer."
      );
    }
  };

  useEffect(() => {
    if (
      isEdit &&
      starId
    ) {
      fetchStarPerformerById(
        starId
      );
    }
  }, [isEdit, starId]);

  // =========================================================
  // LOAD QUESTIONS FOR SELECTED LEVEL
  // =========================================================

  const loadQuestionsForLevel = async (
    performerId,
    levelNumber
  ) => {
    if (
      !performerId ||
      !levelNumber
    ) {
      return;
    }

    setQuestionsLoading(true);

    try {
      const response =
        await getStarPerformerQuestions(
          performerId,
          Number(levelNumber),
          Number(accountId),
          Number(roleId)
        );

      const data =
        response?.data || {};

      const apiQuestions =
        Array.isArray(data.questions)
          ? data.questions
          : [];

      if (
        apiQuestions.length === 0
      ) {
        setQuizzes([
          makeEmptyQuiz(),
        ]);

        return;
      }

      setQuizzes(
        apiQuestions.map(
          (question) => {
            const options =
              Array.isArray(
                question.options
              )
                ? question.options
                : [];

            const correctIndexes =
              options
                .map(
                  (
                    option,
                    index
                  ) =>
                    option.isCorrect
                      ? index
                      : null
                )
                .filter(
                  (index) =>
                    index !== null
                );

            const questionType =
              question.questionTypeId !==
                null &&
                question.questionTypeId !==
                undefined
                ? String(
                  question.questionTypeId
                )
                : "";

            const type =
              getQuestionType(
                questionType
              );

            return {
              id:
                quizIdCounter++,

              questionId:
                question._id ||
                null,

              quiz:
                question.question ||
                "",

              questionType,

              options:
                type ===
                  "trueFalse"
                  ? [
                    "True",
                    "False",
                  ]
                  : options.map(
                    (option) =>
                      option.text ||
                      ""
                  ),

              optionIds:
                type ===
                  "trueFalse"
                  ? [
                    null,
                    null,
                  ]
                  : options.map(
                    (option) =>
                      option._id ||
                      null
                  ),

              correctAnswer:
                correctIndexes.length ===
                  1
                  ? correctIndexes[0]
                  : "",

              correctAnswers:
                correctIndexes,

              isOpen: true,

              isExisting: true,
            };
          }
        )
      );
    } catch (error) {
      console.error(
        "Failed to load questions:",
        error
      );

      message.error(
        error?.response
          ?.data?.message ||
        "Failed to load questions."
      );
    } finally {
      setQuestionsLoading(
        false
      );
    }
  };

  // =========================================================
  // PREFILL EDIT MODE
  // =========================================================

  useEffect(() => {
    if (
      !isEdit ||
      !initialStarData
    ) {
      return;
    }

    const data =
      initialStarData;

    setFullName(
      data.fullName || ""
    );

    setDesignation(
      data.designation || ""
    );

    setLanguage(
      data.defaultLanguageId !==
        undefined &&
        data.defaultLanguageId !==
        null
        ? String(
          data.defaultLanguageId
        )
        : ""
    );

    setAvatar(
      data.profileImage || null
    );

    setIsStarActive(
      data.isActive !==
        undefined
        ? data.isActive
        : true
    );

    // =======================================================
    // KEEP COMPLETE CONTENTS IN STATE
    // =======================================================

    const loadedContents =
      Array.isArray(
        data.contents
      )
        ? data.contents
        : [];

    setContents(
      loadedContents
    );

    const firstContent =
      loadedContents[0] || {};

    const video =
      firstContent.video || {};

    setSelectedLevelNumber(
      firstContent.levelNumber
        ? String(
          firstContent.levelNumber
        )
        : ""
    );

    setVideoCode(
      video.videoCode || ""
    );

    setVideoTitle(
      video.title || ""
    );

    setThumbnailCode(
      video.thumbnailCode ||
      ""
    );

    setVideoLanguage(
      video.languageId !==
        undefined &&
        video.languageId !==
        null
        ? String(
          video.languageId
        )
        : ""
    );

    setVideoDescription(
      video.description || ""
    );
    // Get video duration from the GET API response
    setVideoDuration(
      video.duration ??
      video.videoDuration ??
      ""
    );

    const firstQuestion =
      Array.isArray(
        firstContent.quizzes
      )
        ? firstContent.quizzes[0]
        : null;

    setWeightage(
      firstQuestion?.weightage !==
        undefined &&
        firstQuestion?.weightage !==
        null
        ? String(
          firstQuestion.weightage
        )
        : ""
    );

    if (
      firstContent.levelNumber
    ) {
      loadQuestionsForLevel(
        starId,
        firstContent.levelNumber
      );
    }
  }, [
    isEdit,
    initialStarData,
    starId,
  ]);

  // =========================================================
  // UPDATE CONTENTS STATE
  //
  // This keeps video changes ready in state.
  // It does NOT send them to PATCH yet.
  // =========================================================

  const updateCurrentLevelContents = (
    updates = {}
  ) => {
    if (!selectedLevelNumber) {
      return;
    }

    const levelNumber =
      Number(
        selectedLevelNumber
      );

    setContents(
      (prev) => {
        const existingIndex =
          prev.findIndex(
            (item) =>
              Number(
                item.levelNumber
              ) ===
              levelNumber
          );

        const existingContent =
          existingIndex >= 0
            ? prev[
            existingIndex
            ]
            : {
              levelNumber,
              levelName:
                levels.find(
                  (level) =>
                    Number(
                      level.levelNumber
                    ) ===
                    levelNumber
                )?.title ||
                "",
              video: {},
              quizzes: [],
            };

        const updatedContent =
        {
          ...existingContent,
          ...updates,
        };

        if (
          existingIndex ===
          -1
        ) {
          return [
            ...prev,
            updatedContent,
          ];
        }

        return prev.map(
          (item, index) =>
            index ===
              existingIndex
              ? updatedContent
              : item
        );
      }
    );
  };

  // =========================================================
  // LEVEL CHANGE
  //
  // Edit mode does not allow level changes.
  // This remains available for create mode.
  // =========================================================

  const handleLevelChange = async (
    value
  ) => {
    if (isEdit) {
      return;
    }

    setSelectedLevelNumber(
      value
    );

    clearError(
      "level"
    );
  };

  // =========================================================
  // COMPLETION
  // =========================================================

  const isSection1Complete =
    fullName.trim() !== "" &&
    designation.trim() !== "" &&
    language !== "";

  const isSection2Complete =
    videoCode.trim() !== "" &&
    videoTitle.trim() !== "" &&
    thumbnailCode.trim() !== "" &&
    videoLanguage !== "" &&
    videoDescription.trim() !== "";

  const isQuizComplete = (
    q
  ) => {
    if (
      q.quiz.trim() === "" ||
      q.questionType === "" ||
      q.options.length === 0 ||
      q.options.some(
        (opt) =>
          opt.trim() === ""
      )
    ) {
      return false;
    }

    const type =
      getQuestionType(
        q.questionType
      );

    if (
      type === "multiple"
    ) {
      return (
        Array.isArray(
          q.correctAnswers
        ) &&
        q.correctAnswers.length >
        0
      );
    }

    return (
      q.correctAnswer !==
      "" &&
      q.correctAnswer !==
      null &&
      q.correctAnswer !==
      undefined
    );
  };

  const isSection3Complete =
    quizzes.length > 0 &&
    quizzes.every(
      isQuizComplete
    );

  const isSection4Complete =
    weightage !== "" &&
    Number(weightage) > 0;

  const sectionComplete = {
    1: isSection1Complete,
    2: isSection2Complete,
    3: isSection3Complete,
    4: isSection4Complete,
  };

  // =========================================================
  // AVATAR
  // =========================================================

  const handleAvatarChange = (
    e
  ) => {
    const file =
      e.target.files?.[0];

    if (file) {
      setProfileImage(
        file
      );

      setAvatar(
        URL.createObjectURL(
          file
        )
      );
    }
  };

  // =========================================================
  // HELPERS
  // =========================================================

  const toSentenceCase = (
    str
  ) => {
    if (!str) return str;

    const lower =
      str.toLowerCase();

    return (
      lower.charAt(0)
        .toUpperCase() +
      lower.slice(1)
    );
  };

  const clearError = (
    field
  ) => {
    setErrors(
      (prev) =>
        prev[field]
          ? {
            ...prev,
            [field]: "",
          }
          : prev
    );
  };

  const clearQuizError = (
    quizIndex,
    field,
    optionIndex = null
  ) => {
    setErrors((prev) => {
      const updatedQuizzes = [...prev.quizzes];

      if (!updatedQuizzes[quizIndex]) {
        return prev;
      }

      if (
        field === "options" &&
        optionIndex !== null
      ) {
        const updatedOptions = [
          ...(updatedQuizzes[quizIndex].options || []),
        ];

        updatedOptions[optionIndex] = "";

        updatedQuizzes[quizIndex] = {
          ...updatedQuizzes[quizIndex],
          options: updatedOptions,
        };
      } else {
        updatedQuizzes[quizIndex] = {
          ...updatedQuizzes[quizIndex],
          [field]: "",
        };
      }

      return {
        ...prev,
        quizzes: updatedQuizzes,
      };
    });
  };

  // =========================================================
  // QUIZ HANDLERS
  // =========================================================

  const toggleQuiz = (
    id
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (quiz) =>
            quiz.id === id
              ? {
                ...quiz,
                isOpen:
                  !quiz.isOpen,
              }
              : quiz
        )
    );
  };

  // =========================================================
  // UPDATE QUIZ
  // =========================================================

  const updateQuiz = (
    index,
    field,
    value
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (q, i) => {
            if (
              i !== index
            ) {
              return q;
            }

            // -----------------------------------------------
            // QUESTION TYPE CHANGE
            // -----------------------------------------------

            if (
              field ===
              "questionType"
            ) {
              const type =
                getQuestionType(
                  value
                );

              const previousType =
                getQuestionType(
                  q.questionType
                );

              // =====================================================
              // TRUE / FALSE
              // =====================================================

              if (
                type ===
                "trueFalse"
              ) {
                return {
                  ...q,

                  questionType:
                    value,

                  options: [
                    "True",
                    "False",
                  ],

                  optionIds: [
                    null,
                    null,
                  ],

                  correctAnswer:
                    "",

                  correctAnswers:
                    [],
                };
              }

              // =====================================================
              // SINGLE / MULTIPLE
              // =====================================================

              // If we are coming FROM True/False,
              // restore the normal 4 options.
              if (
                previousType ===
                "trueFalse"
              ) {
                return {
                  ...q,

                  questionType:
                    value,

                  options: [
                    "",
                    "",
                    "",
                    "",
                  ],

                  optionIds: [
                    null,
                    null,
                    null,
                    null,
                  ],

                  correctAnswer:
                    "",

                  correctAnswers:
                    [],
                };
              }

              // =====================================================
              // SINGLE <-> MULTIPLE
              //
              // Keep the existing options.
              // =====================================================

              return {
                ...q,

                questionType:
                  value,

                options:
                  q.options.length >=
                    4
                    ? q.options
                    : [
                      ...q.options,
                      ...Array(
                        4 -
                        q.options.length
                      ).fill(""),
                    ],

                optionIds:
                  q.optionIds?.length >=
                    4
                    ? q.optionIds
                    : [
                      ...(q.optionIds || []),
                      ...Array(
                        4 -
                        (q.optionIds?.length || 0)
                      ).fill(null),
                    ],

                correctAnswer:
                  "",

                correctAnswers:
                  [],
              };
            }

            return {
              ...q,
              [field]:
                value,
            };
          }
        )
    );
  };

  // =========================================================
  // UPDATE QUIZ OPTION
  // =========================================================

  const updateQuizOption = (
    quizIndex,
    optionIndex,
    value
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (q, i) => {
            if (
              i !==
              quizIndex
            ) {
              return q;
            }

            // True/False options are generated automatically.
            if (
              getQuestionType(
                q.questionType
              ) ===
              "trueFalse"
            ) {
              return q;
            }

            const updatedOptions =
              [
                ...q.options,
              ];

            updatedOptions[
              optionIndex
            ] = value;

            return {
              ...q,
              options:
                updatedOptions,
            };
          }
        )
    );
  };

  // =========================================================
  // MULTIPLE CORRECT ANSWER
  // =========================================================

  const toggleCorrectAnswer = (
    quizIndex,
    optionIndex
  ) => {
    setQuizzes(
      (prev) =>
        prev.map(
          (quiz, index) => {
            if (
              index !==
              quizIndex
            ) {
              return quiz;
            }

            const currentAnswers =
              Array.isArray(
                quiz.correctAnswers
              )
                ? quiz.correctAnswers
                : [];

            const updatedAnswers =
              currentAnswers.includes(
                optionIndex
              )
                ? currentAnswers.filter(
                  (
                    answer
                  ) =>
                    answer !==
                    optionIndex
                )
                : [
                  ...currentAnswers,
                  optionIndex,
                ];

            return {
              ...quiz,
              correctAnswers:
                updatedAnswers,
              correctAnswer:
                "",
            };
          }
        )
    );
  };

  // =========================================================
  // ADD OPTION
  // =========================================================

  const handleAddOptionToQuiz = (
    quizIndex
  ) => {
    const quiz =
      quizzes[quizIndex];

    if (!quiz) {
      return;
    }

    // True/False must always have exactly two options.
    if (
      getQuestionType(
        quiz.questionType
      ) === "trueFalse"
    ) {
      return;
    }

    setQuizzes(
      (prev) =>
        prev.map(
          (q, i) =>
            i === quizIndex
              ? {
                ...q,

                options: [
                  ...q.options,
                  "",
                ],

                optionIds: [
                  ...q.optionIds,
                  null,
                ],
              }
              : q
        )
    );
  };
  const handleDeleteQuizOption = (qIndex, optIndex) => {
    setQuizzes((prevQuizzes) => {
      const updatedQuizzes = [...prevQuizzes];

      // Do not allow deleting the first 4 options
      if (updatedQuizzes[qIndex].options.length <= 4) {
        return prevQuizzes;
      }

      updatedQuizzes[qIndex] = {
        ...updatedQuizzes[qIndex],
        options: updatedQuizzes[qIndex].options.filter(
          (_, index) => index !== optIndex
        ),
      };

      return updatedQuizzes;
    });
  };

  // =========================================================
  // ADD QUIZ
  // =========================================================

  const handleAddQuiz = () => {
    const lastQuiz =
      quizzes[
      quizzes.length - 1
      ];

    if (
      !isQuizComplete(
        lastQuiz
      )
    ) {
      message.warning(
        "Please fill all the mandatory fields before adding another quiz."
      );

      validateForm();

      return;
    }

    setQuizzes(
      (prev) => [
        ...prev.map(
          (q) => ({
            ...q,
            isOpen: false,
          })
        ),

        {
          ...makeEmptyQuiz(),
          isOpen: true,
        },
      ]
    );
  };

  // =========================================================
  // REMOVE QUIZ
  // =========================================================

  const handleRemoveQuiz = (
    index
  ) => {
    const quiz =
      quizzes[index];

    if (
      isEdit &&
      quiz?.questionId
    ) {
      handleDeleteQuestion(
        quiz
      );

      return;
    }

    setQuizzes(
      (prev) =>
        prev.filter(
          (_, i) =>
            i !== index
        )
    );
  };

  // =========================================================
  // VALIDATION
  // =========================================================

  const validateForm = () => {
    const newErrors = {
      fullName:
        toSentenceCase(
          fullName.trim() ===
            ""
            ? "Full name is required"
            : ""
        ),

      designation:
        toSentenceCase(
          designation.trim() ===
            ""
            ? "Designation is required"
            : ""
        ),

      language:
        toSentenceCase(
          language === ""
            ? "Language is required"
            : ""
        ),

      level:
        toSentenceCase(
          selectedLevelNumber ===
            ""
            ? "Level is required"
            : ""
        ),

      videoCode:
        toSentenceCase(
          videoCode.trim() ===
            ""
            ? "Video code is required"
            : ""
        ),

      videoTitle:
        toSentenceCase(
          videoTitle.trim() ===
            ""
            ? "Video title is required"
            : ""
        ),

      thumbnailCode:
        toSentenceCase(
          thumbnailCode.trim() ===
            ""
            ? "Video thumbnail code is required"
            : ""
        ),

      videoLanguage:
        toSentenceCase(
          videoLanguage ===
            ""
            ? "Video language is required"
            : ""
        ),

      videoDescription:
        toSentenceCase(
          videoDescription.trim() ===
            ""
            ? "Video description is required"
            : ""
        ),

      videoDuration:
        toSentenceCase(
          videoDuration.trim() === ""
            ? "Video duration is required"
            : ""
        ),

      weightage:
        toSentenceCase(
          weightage ===
            ""
            ? "Weightage is required"
            : Number(
              weightage
            ) <= 0
              ? "Weightage must be greater than 0"
              : ""
        ),

      quizzes:
        quizzes.map(
          (q) => {
            const type =
              getQuestionType(
                q.questionType
              );

            const isMultiple =
              type ===
              "multiple";

            return {
              quiz:
                toSentenceCase(
                  q.quiz.trim() ===
                    ""
                    ? "Quiz question is required"
                    : ""
                ),

              questionType:
                toSentenceCase(
                  q.questionType ===
                    ""
                    ? "Question type is required"
                    : ""
                ),

              options:
                q.options.map(
                  (opt) =>
                    toSentenceCase(
                      opt.trim() ===
                        ""
                        ? "This option is required"
                        : ""
                    )
                ),

              correctAnswer:
                isMultiple
                  ? ""
                  : toSentenceCase(
                    q.correctAnswer ===
                      "" ||
                      q.correctAnswer ===
                      null ||
                      q.correctAnswer ===
                      undefined
                      ? "Select the correct answer"
                      : ""
                  ),

              correctAnswers:
                isMultiple
                  ? !Array.isArray(
                    q.correctAnswers
                  ) ||
                    q.correctAnswers.length ===
                    0
                    ? "Select at least one correct answer"
                    : ""
                  : "",
            };
          }
        ),
    };

    setErrors(
      newErrors
    );

    const hasSimpleFieldError =
      [
        newErrors.fullName,
        newErrors.designation,
        newErrors.language,
        newErrors.level,
        newErrors.videoCode,
        newErrors.videoTitle,
        newErrors.thumbnailCode,
        newErrors.videoLanguage,
        newErrors.videoDescription,
        newErrors.weightage,
      ].some(
        (msg) =>
          msg !== ""
      );

    const hasQuizError =
      newErrors.quizzes.some(
        (qErr) =>
          qErr.quiz ||
          qErr.questionType ||
          qErr.correctAnswer ||
          qErr.correctAnswers ||
          qErr.options.some(
            (optMsg) =>
              optMsg !== ""
          )
      );

    return (
      !hasSimpleFieldError &&
      !hasQuizError
    );
  };

  // =========================================================
  // VIDEO CODE
  // =========================================================

  const normalizeVideoCode = (
    input
  ) => {
    if (!input)
      return "";

    const trimmed =
      input.trim();

    if (
      trimmed.includes(
        "youtube.com"
      ) ||
      trimmed.includes(
        "youtu.be"
      )
    ) {
      try {
        const url =
          new URL(
            trimmed.includes(
              "://"
            )
              ? trimmed
              : `https://${trimmed}`
          );

        if (
          url.hostname.includes(
            "youtu.be"
          )
        ) {
          return url.pathname.replace(
            "/",
            ""
          );
        }

        if (
          url.searchParams.has(
            "v"
          )
        ) {
          return (
            url.searchParams.get(
              "v"
            ) || ""
          );
        }
      } catch {
        // Fall through.
      }
    }

    if (
      trimmed.includes("&")
    ) {
      return trimmed.split(
        "&"
      )[0];
    }

    return trimmed;
  };

  // =========================================================
  // BUILD QUESTION PAYLOAD
  // =========================================================

  const buildQuestionPayload = (
    quiz
  ) => {
    const type =
      getQuestionType(
        quiz.questionType
      );

    return {
      question:
        quiz.quiz.trim(),

      languageId:
        Number(
          quiz.questionLanguageId ||
          language
        ),

      questionTypeId:
        Number(
          quiz.questionType
        ),

      weightage:
        Number(
          weightage
        ),

      options:
        quiz.options.map(
          (
            option,
            index
          ) => ({
            ...(quiz.optionIds?.[
              index
            ]
              ? {
                _id:
                  quiz
                    .optionIds[
                  index
                  ],
              }
              : {}),

            text:
              option.trim(),

            isCorrect:
              type ===
                "multiple"
                ? quiz.correctAnswers?.includes(
                  index
                )
                : quiz.correctAnswer ===
                index,
          })
        ),
    };
  };

  // =========================================================
  // ADD QUESTION
  // =========================================================

  const handleAddQuestion = async (
    quiz
  ) => {
    if (
      !starId ||
      !selectedLevelNumber
    ) {
      message.error(
        "Performer and level are required."
      );

      return;
    }

    try {
      await addStarPerformerQuestion(
        starId,
        Number(
          selectedLevelNumber
        ),
        buildQuestionPayload(
          quiz
        ),
        {
          adminClientId:
            Number(
              accountId
            ),

          adminRoleId:
            Number(
              roleId
            ),

          changedBy:
            changedBy !==
              undefined &&
              changedBy !==
              null
              ? Number(
                changedBy
              )
              : undefined,
        }
      );
    } catch (error) {
      throw error;
    }
  };

  // =========================================================
  // UPDATE QUESTION
  // =========================================================

  const handleUpdateQuestion =
    async (
      quiz
    ) => {
      if (
        !starId ||
        !selectedLevelNumber ||
        !quiz.questionId
      ) {
        return;
      }

      await updateStarPerformerQuestion(
        starId,
        Number(
          selectedLevelNumber
        ),
        quiz.questionId,
        buildQuestionPayload(
          quiz
        ),
        {
          adminClientId:
            Number(
              accountId
            ),

          adminRoleId:
            Number(
              roleId
            ),

          changedBy:
            changedBy !==
              undefined &&
              changedBy !==
              null
              ? Number(
                changedBy
              )
              : undefined,
        }
      );
    };

  // =========================================================
  // DELETE QUESTION
  // =========================================================

  const handleDeleteQuestion =
    async (
      quiz
    ) => {
      if (
        !starId ||
        !selectedLevelNumber ||
        !quiz.questionId
      ) {
        setQuizzes(
          (prev) =>
            prev.filter(
              (item) =>
                item.id !==
                quiz.id
            )
        );

        return;
      }

      try {
        await deleteStarPerformerQuestion(
          starId,
          Number(
            selectedLevelNumber
          ),
          quiz.questionId,
          {
            adminClientId:
              Number(
                accountId
              ),

            adminRoleId:
              Number(
                roleId
              ),

            changedBy:
              changedBy !==
                undefined &&
                changedBy !==
                null
                ? Number(
                  changedBy
                )
                : undefined,
          }
        );

        setQuizzes(
          (prev) =>
            prev.filter(
              (item) =>
                item.id !==
                quiz.id
            )
        );

        message.success(
          "Question deleted successfully."
        );
      } catch (error) {
        console.error(
          "Failed to delete question:",
          error
        );

        message.error(
          error?.response
            ?.data
            ?.message ||
          "Failed to delete question."
        );
      }
    };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave =
    async () => {
      const isValid =
        validateForm();

      if (!isValid) {
        message.destroy();

        message.warning(
          "Fill all the mandatory fields."
        );

        return;
      }

      const normalizedVideoCode =
        normalizeVideoCode(
          videoCode
        );

      if (
        !normalizedVideoCode
      ) {
        setErrors(
          (prev) => ({
            ...prev,
            videoCode:
              "Video code is required",
          })
        );

        message.error(
          "Provide a valid YouTube video code or URL."
        );

        return;
      }

      if (
        !accountId ||
        !roleId
      ) {
        message.error(
          "Client ID and Role ID are required."
        );

        return;
      }

      try {
        // ====================================================
        // CREATE
        // ====================================================

        if (!isEdit) {
          const payload = {
            clientId:
              String(
                accountId
              ),

            roleId:
              String(
                roleId
              ),

            fullName:
              fullName.trim(),

            designation:
              designation.trim(),

            defaultLanguageId:
              Number(
                language
              ),

            isActive:
              isStarActive,

            contents: [
              {
                levelNumber:
                  Number(
                    selectedLevelNumber
                  ),

                video: {
                  videoCode:
                    normalizedVideoCode,

                  title:
                    videoTitle.trim(),

                  thumbnailCode:
                    thumbnailCode.trim(),

                  duration: videoDuration.trim(),

                  languageId:
                    Number(
                      videoLanguage
                    ),

                  description:
                    videoDescription.trim(),
                },

                quizzes:
                  quizzes.map(
                    (
                      quiz
                    ) => {
                      const type =
                        getQuestionType(
                          quiz.questionType
                        );

                      return {
                        question:
                          quiz.quiz.trim(),

                        languageId:
                          Number(
                            quiz.questionLanguageId ||
                            language
                          ),

                        questionTypeId:
                          Number(
                            quiz.questionType
                          ),

                        weightage:
                          Number(
                            weightage
                          ),

                        options:
                          quiz.options.map(
                            (
                              option,
                              index
                            ) => ({
                              text:
                                option.trim(),

                              isCorrect:
                                type ===
                                  "multiple"
                                  ? quiz.correctAnswers?.includes(
                                    index
                                  )
                                  : quiz.correctAnswer ===
                                  index,
                            })
                          ),
                      };
                    }
                  ),
              },
            ],
          };

          await createStarPerformer(
            payload,
            {
              adminClientId:
                Number(
                  accountId
                ),

              adminRoleId:
                Number(
                  roleId
                ),

              profileImage: profileImage,

              changedBy:
                changedBy !==
                  undefined &&
                  changedBy !==
                  null
                  ? Number(
                    changedBy
                  )
                  : undefined,
            }
          );

          message.success(
            "Star performer added successfully!"
          );

          navigate(-1);

          return;
        }

        // ====================================================
        // EDIT PERFORMER
        //
        // IMPORTANT:
        // Current backend PATCH does NOT support contents.
        //
        // Therefore:
        // - contents stays in frontend state
        // - video changes stay in contents state
        // - contents is NOT sent in this PATCH
        //
        // When backend supports contents:
        // simply add:
        //
        // contents,
        //
        // inside performerPayload.
        // ====================================================

        if (!starId) {
          throw new Error(
            "Star performer ID is required for update."
          );
        }

        const performerPayload = {
          fullName:
            fullName.trim(),

          designation:
            designation.trim(),

          defaultLanguageId:
            Number(
              language
            ),

          isActive:
            isStarActive,

          contents: contents,
        };

        await updateStarPerformer(
          starId,
          performerPayload,
          {
            adminClientId:
              Number(
                accountId
              ),

            adminRoleId:
              Number(
                roleId
              ),

            ...(profileImage
              ? {
                profileImage:
                  profileImage,
              }
              : {}),

            changedBy:
              changedBy !==
                undefined &&
                changedBy !==
                null
                ? Number(
                  changedBy
                )
                : undefined,
          }
        );

        // ====================================================
        // QUESTION CHANGES
        //
        // Existing questions -> PATCH
        // New questions -> POST
        //
        // Delete is handled immediately by delete button.
        // ====================================================

        for (
          const quiz of quizzes
        ) {
          if (
            !quiz.quiz.trim()
          ) {
            continue;
          }

          if (
            quiz.questionId
          ) {
            await handleUpdateQuestion(
              quiz
            );
          } else {
            await handleAddQuestion(
              quiz
            );
          }
        }

        message.success(
          "Star performer updated successfully!"
        );

        navigate(-1);
      } catch (error) {
        console.error(
          "Error saving Star Performer:",
          error
        );

        const apiMessage =
          error?.response
            ?.data?.message ||
          error?.response
            ?.data?.errors ||
          "Unable to save Star Performer.";

        message.error(
          typeof apiMessage ===
            "string"
            ? apiMessage
            : "Option text must be unique."
        );
      }
    };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="min-h-screen bg-[#F6F6F6]">
      <ReusableHeader />

      <div className="px-8 pt-18 pb-2 -mt-[30px]">
        {/* Back */}
        <button
          onClick={() =>
            navigate(-1)
          }
          className="flex items-center gap-2 text-red-700 font-medium"
        >
          <FaArrowLeft />
          Back
        </button>

        {/* Heading */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-xl font-semibold">
            {isEdit
              ? "Edit Role Overview Videos Configuration"
              : "Add Role Overview Videos Configuration"}
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <button
                type="button"
                onClick={() =>
                  setIsStarActive(
                    (prev) =>
                      !prev
                  )
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isStarActive
                  ? "bg-[#D50000]"
                  : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isStarActive
                    ? "translate-x-6"
                    : "translate-x-1"
                    }`}
                />
              </button>

              <span className="text-sm font-medium text-[#1B1B1B]">
                Active
              </span>
            </label>
          )}
        </div>

        {/* =====================================================
            SELECT ROLE JOURNEY LEVEL
        ===================================================== */}

        <div className="mt-5 w-[300px]">
          <label className="block text-[15px] font-medium text-[#1B1B1B] mb-2">
            Select{" "}
            <span className="text-red-600">
              *
            </span>
          </label>

          <Select
            showSearch
            value={
              selectedLevelNumber ||
              undefined
            }
            onChange={
              handleLevelChange
            }
            disabled={isEdit}
            placeholder="Select the level"
            loading={levelsLoading}
            optionFilterProp="label"
            filterOption={(
              input,
              option
            ) =>
              (
                option?.label ||
                ""
              )
                .toLowerCase()
                .includes(
                  input.toLowerCase()
                )
            }
            options={levels.map(
              (level) => ({
                value:
                  String(
                    level.levelNumber
                  ),
                label:
                  level.title,
              })
            )}
            allowClear={!isEdit}
            className="w-full"
          />

          {errors.level && (
            <p className="text-red-600 text-xs mt-1">
              {errors.level}
            </p>
          )}
        </div>

        {/* =====================================================
            ABOUT PERFORMER
        ===================================================== */}

        <div className="mt-5 bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-base font-bold text-[#1B1B1B] mb-4">
            About Performer
          </h3>

          <div className="flex gap-6 items-start">
            <div className="relative w-[80px] h-[80px] shrink-0">
              <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FaUser
                    className="text-gray-400"
                    size={30}
                  />
                )}
              </div>

              <label className="absolute bottom-0 right-0 bg-white rounded-full p-1.5 shadow cursor-pointer">
                <FaCamera
                  size={11}
                  className="text-[#1B1B1B]"
                />

                <input
                  type="file"
                  accept="image/*"
                  onChange={
                    handleAvatarChange
                  }
                  className="hidden"
                />
              </label>
            </div>

            <div className="flex-1 grid grid-cols-2 gap-x-6 gap-y-3">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Full Name{" "}
                  <span className="text-red-600">
                    *
                  </span>
                </label>

                <input
                  type="text"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(
                    e
                  ) => {
                    setFullName(
                      e.target
                        .value
                    );

                    clearError(
                      "fullName"
                    );
                  }}
                  className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.fullName
                    ? "border-red-600"
                    : "border-gray-300"
                    }`}
                />

                {errors.fullName && (
                  <p className="text-red-600 text-xs mt-1">
                    {
                      errors.fullName
                    }
                  </p>
                )}
              </div>

              {/* Designation */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Designation{" "}
                  <span className="text-red-600">
                    *
                  </span>
                </label>

                <input
                  type="text"
                  placeholder="Enter Designation"
                  value={
                    designation
                  }
                  onChange={(
                    e
                  ) => {
                    setDesignation(
                      e.target
                        .value
                    );

                    clearError(
                      "designation"
                    );
                  }}
                  className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.designation
                    ? "border-red-600"
                    : "border-gray-300"
                    }`}
                />

                {errors.designation && (
                  <p className="text-red-600 text-xs mt-1">
                    {
                      errors.designation
                    }
                  </p>
                )}
              </div>

              {/* Default Language */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Language{" "}
                  <span className="text-red-600">
                    *
                  </span>
                </label>

                <Select
                  showSearch
                  value={
                    language ||
                    undefined
                  }
                  loading={
                    optionsLoading
                  }
                  onChange={(
                    value
                  ) => {
                    setLanguage(
                      value
                    );

                    clearError(
                      "language"
                    );
                  }}
                  placeholder="Select Language"
                  optionFilterProp="label"
                  filterOption={(
                    input,
                    option
                  ) =>
                    (
                      option?.label ||
                      ""
                    )
                      .toLowerCase()
                      .includes(
                        input.toLowerCase()
                      )
                  }
                  options={languages.map(
                    (
                      item
                    ) => ({
                      value:
                        String(
                          item.id
                        ),
                      label:
                        item.name,
                    })
                  )}
                  allowClear
                  className="w-full"
                />

                {errors.language && (
                  <p className="text-red-600 text-xs mt-1">
                    {
                      errors.language
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            VIDEO DETAILS
        ===================================================== */}

        <div className="mt-5 bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-base font-bold text-[#1B1B1B] mb-4">
            Video Details
          </h3>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {/* Video Code */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Code{" "}
                <span className="text-red-600">
                  *
                </span>
              </label>

              <input
                type="text"
                placeholder="Enter Video Code"
                value={
                  videoCode
                }
                onChange={(
                  e
                ) => {
                  const value =
                    e.target.value;

                  const normalizedCode =
                    normalizeVideoCode(
                      value
                    );

                  const isAutoGenerated =
                    thumbnailCode &&
                    thumbnailCode.includes(
                      "https://img.youtube.com/vi/"
                    );

                  const shouldSetThumbnail =
                    normalizedCode &&
                    (isAutoGenerated ||
                      !thumbnailCode);

                  const newThumbnail =
                    shouldSetThumbnail
                      ? `https://img.youtube.com/vi/${normalizedCode}/0.jpg`
                      : thumbnailCode;

                  setVideoCode(
                    value
                  );

                  if (shouldSetThumbnail) {
                    setThumbnailCode(
                      newThumbnail
                    );
                  }

                  updateCurrentLevelContents(
                    {
                      video: {
                        ...(
                          contents.find(
                            (item) =>
                              Number(
                                item.levelNumber
                              ) ===
                              Number(
                                selectedLevelNumber
                              )
                          )?.video ||
                          {}
                        ),
                        videoCode:
                          normalizedCode,
                        thumbnailCode:
                          newThumbnail,
                      },
                    }
                  );

                  clearError(
                    "videoCode"
                  );
                }}
                className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.videoCode
                  ? "border-red-600"
                  : "border-gray-300"
                  } ${isEdit
                    ? "bg-gray-100"
                    : ""
                  }`}
              />

              {errors.videoCode && (
                <p className="text-red-600 text-xs mt-1">
                  {
                    errors.videoCode
                  }
                </p>
              )}
            </div>

            {/* Video Title */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Title{" "}
                <span className="text-red-600">
                  *
                </span>
              </label>

              <input
                type="text"
                placeholder="Enter Video Title"
                value={
                  videoTitle
                }
                onChange={(
                  e
                ) => {
                  const value =
                    e.target.value;

                  setVideoTitle(
                    value
                  );

                  updateCurrentLevelContents(
                    {
                      video: {
                        ...(
                          contents.find(
                            (item) =>
                              Number(
                                item.levelNumber
                              ) ===
                              Number(
                                selectedLevelNumber
                              )
                          )?.video ||
                          {}
                        ),
                        title:
                          value,
                      },
                    }
                  );

                  clearError(
                    "videoTitle"
                  );
                }}
                className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.videoTitle
                  ? "border-red-600"
                  : "border-gray-300"
                  } ${isEdit
                    ? "bg-gray-100"
                    : ""
                  }`}
              />

              {errors.videoTitle && (
                <p className="text-red-600 text-xs mt-1">
                  {
                    errors.videoTitle
                  }
                </p>
              )}
            </div>

            {/* Thumbnail */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Thumbnail Code{" "}
                <span className="text-red-600">
                  *
                </span>
              </label>

              <input
                type="text"
                placeholder="Enter Thumbnail Code"
                disabled={true}
                value={
                  thumbnailCode
                }
                onChange={(
                  e
                ) => {
                  const value =
                    e.target.value;

                  setThumbnailCode(
                    value
                  );

                  updateCurrentLevelContents(
                    {
                      video: {
                        ...(
                          contents.find(
                            (item) =>
                              Number(
                                item.levelNumber
                              ) ===
                              Number(
                                selectedLevelNumber
                              )
                          )?.video ||
                          {}
                        ),
                        thumbnailCode:
                          value,
                      },
                    }
                  );

                  clearError(
                    "thumbnailCode"
                  );
                }}
                className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.thumbnailCode
                  ? "border-red-600"
                  : "border-gray-300"
                  } ${isEdit
                    ? "bg-gray-100"
                    : ""
                  }`}
              />

              {errors.thumbnailCode && (
                <p className="text-red-600 text-xs mt-1">
                  {
                    errors.thumbnailCode
                  }
                </p>
              )}
            </div>

            {/* Video Language */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Language{" "}
                <span className="text-red-600">
                  *
                </span>
              </label>

              <Select

                value={
                  videoLanguage ||
                  undefined
                }
                loading={
                  optionsLoading
                }
                onChange={(
                  value
                ) => {
                  setVideoLanguage(
                    value
                  );

                  updateCurrentLevelContents(
                    {
                      video: {
                        ...(
                          contents.find(
                            (item) =>
                              Number(
                                item.levelNumber
                              ) ===
                              Number(
                                selectedLevelNumber
                              )
                          )?.video ||
                          {}
                        ),
                        languageId:
                          Number(
                            value
                          ),
                      },
                    }
                  );

                  clearError(
                    "videoLanguage"
                  );
                }}
                placeholder="Select Language"
                optionFilterProp="label"
                filterOption={(
                  input,
                  option
                ) =>
                  (
                    option?.label ||
                    ""
                  )
                    .toLowerCase()
                    .includes(
                      input.toLowerCase()
                    )
                }
                options={videoLanguages.map(
                  (
                    item
                  ) => ({
                    value:
                      String(
                        item.id
                      ),
                    label:
                      item.name,
                  })
                )}
                allowClear
                className="w-full"
              />

              {errors.videoLanguage && (
                <p className="text-red-600 text-xs mt-1">
                  {
                    errors.videoLanguage
                  }
                </p>
              )}
            </div>

            {/* Video Duration */}
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Video Duration{" "}
                <span className="text-red-600">*</span>
              </label>

              <input
                type="text"
                placeholder="MM:SS"
                value={videoDuration || ""}
                maxLength={5}
                onChange={(e) => {
                  let value = e.target.value;

                  // Allow only numbers
                  value = value.replace(/[^0-9]/g, "");

                  // Limit to 4 digits (MMSS)
                  value = value.slice(0, 4);

                  // Automatically add colon after MM
                  if (value.length > 2) {
                    value = value.slice(0, 2) + ":" + value.slice(2);
                  }

                  setVideoDuration(value);

                  updateCurrentLevelContents({
                    video: {
                      ...(
                        contents.find(
                          (item) =>
                            Number(item.levelNumber) ===
                            Number(selectedLevelNumber)
                        )?.video || {}
                      ),
                      duration: value,
                    },
                  });

                  clearError("videoDuration");
                }}
                className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.videoDuration
                    ? "border-red-600"
                    : "border-gray-300"
                  }`}
              />

              {errors.videoDuration && (
                <p className="text-red-600 text-xs mt-1">
                  {errors.videoDuration}
                </p>
              )}
            </div>


            {/* Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                Video Description{" "}
                <span className="text-red-600">
                  *
                </span>
              </label>

              <textarea
                placeholder="Enter Video Description"
                value={
                  videoDescription
                }
                onChange={(
                  e
                ) => {
                  const value =
                    e.target.value;

                  setVideoDescription(
                    value
                  );

                  updateCurrentLevelContents(
                    {
                      video: {
                        ...(
                          contents.find(
                            (item) =>
                              Number(
                                item.levelNumber
                              ) ===
                              Number(
                                selectedLevelNumber
                              )
                          )?.video ||
                          {}
                        ),
                        description:
                          value,
                      },
                    }
                  );

                  clearError(
                    "videoDescription"
                  );
                }}
                rows={3}
                className={`w-full border rounded-md px-3 py-2 outline-none text-sm resize-none ${errors.videoDescription
                  ? "border-red-600"
                  : "border-gray-300"
                  } ${isEdit
                    ? "bg-gray-100"
                    : ""
                  }`}
              />

              {errors.videoDescription && (
                <p className="text-red-600 text-xs mt-1">
                  {
                    errors.videoDescription
                  }
                </p>
              )}
            </div>
          </div>
        </div>

        {/* =====================================================
            QUIZ QUESTIONS
        ===================================================== */}

        {quizzes.map(
          (
            q,
            qIndex
          ) => {
            const quizErr =
              errors.quizzes[
              qIndex
              ] || {};

            const questionType =
              getQuestionType(
                q.questionType
              );

            const isMultiple =
              questionType ===
              "multiple";

            const isTrueFalse =
              questionType ===
              "trueFalse";

            return (
              <div
                key={
                  q.id
                }
                className="mt-5 bg-white rounded-2xl shadow-sm p-5"
              >
                <div className="flex items-center justify-between border border-gray-200 rounded-lg px-5 py-4 bg-white shadow-sm">
                  <h3 className="font-semibold text-[17px]">
                    Quiz{" "}
                    {qIndex +
                      1}{" "}
                    Questions
                  </h3>

                  <div className="flex items-center gap-3">
                    {!q.isOpen && (
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveQuiz(
                            qIndex
                          )
                        }
                        className="border border-red-600 text-red-600 px-4 py-1 rounded-md text-sm hover:bg-red-50"
                      >
                        Delete
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        toggleQuiz(
                          q.id
                        )
                      }
                      className="w-8 h-8 rounded-full bg-[#FDEAEA] flex items-center justify-center"
                    >
                      {q.isOpen ? (
                        <ChevronUp className="w-4 h-4 text-[#D50000]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#D50000]" />
                      )}
                    </button>
                  </div>
                </div>

                {q.isOpen && (
                  <>
                    {/* =================================================
                        QUESTION + TYPE
                    ================================================= */}

                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4">
                      {/* Question */}
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Quiz{" "}
                          <span className="text-red-600">
                            *
                          </span>
                        </label>

                        <input
                          type="text"
                          placeholder="Enter Quiz Questions"
                          value={q.quiz}
                          onChange={(e) => {
                            updateQuiz(
                              qIndex,
                              "quiz",
                              e.target.value
                            );

                            clearQuizError(
                              qIndex,
                              "quiz"
                            );
                          }}
                          className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${quizErr.quiz
                            ? "border-red-600"
                            : "border-gray-300"
                            }`}
                        />

                        {quizErr.quiz && (
                          <p className="text-red-600 text-xs mt-1">
                            {
                              quizErr.quiz
                            }
                          </p>
                        )}
                      </div>

                      {/* Question Type */}
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Question Type{" "}
                          <span className="text-red-600">
                            *
                          </span>
                        </label>

                        <Select
                          showSearch
                          value={
                            q.questionType ||
                            undefined
                          }
                          loading={
                            optionsLoading
                          }
                          onChange={(value) => {
                            updateQuiz(
                              qIndex,
                              "questionType",
                              value
                            );

                            clearQuizError(
                              qIndex,
                              "questionType"
                            );
                          }}
                          placeholder="Select Question Type"
                          optionFilterProp="label"
                          filterOption={(
                            input,
                            option
                          ) =>
                            (
                              option?.label ||
                              ""
                            )
                              .toLowerCase()
                              .includes(
                                input.toLowerCase()
                              )
                          }
                          options={questionTypes.map(
                            (
                              item
                            ) => ({
                              value:
                                String(
                                  item.id
                                ),
                              label:
                                item.name,
                            })
                          )}
                          allowClear
                          className="w-full"
                        />

                        {quizErr.questionType && (
                          <p className="text-red-600 text-xs mt-1">
                            {
                              quizErr.questionType
                            }
                          </p>
                        )}
                      </div>
                    </div>

                    {/* =================================================
                        OPTIONS
                    ================================================= */}

                    <div className="flex items-center justify-between mt-6 mb-2">
                      <p className="text-md font-bold">
                        Options{" "}
                        <span className="text-red-600">
                          *
                        </span>
                      </p>

                      {!isTrueFalse && (
                        <button
                          type="button"
                          onClick={() =>
                            handleAddOptionToQuiz(
                              qIndex
                            )
                          }
                          className="text-red-700 font-semibold text-sm hover:underline"
                        >
                          Add Option
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      {q.options.map((opt, optIndex) => (
                        <div
                          key={optIndex}
                          className="relative"
                        >
                          <label className="block text-sm font-medium mb-1">
                            Option {optIndex + 1}{" "}
                            <span className="text-red-600">*</span>
                          </label>

                          <div className="relative">
                            <input
                              type="text"
                              placeholder={
                                isTrueFalse
                                  ? opt
                                  : `Enter Option ${optIndex + 1}`
                              }
                              value={opt}
                              disabled={isTrueFalse}
                              onChange={(e) => {
                                updateQuizOption(
                                  qIndex,
                                  optIndex,
                                  e.target.value
                                );

                                clearQuizError(
                                  qIndex,
                                  "options",
                                  optIndex
                                );
                              }}
                              className={`w-full h-9 border rounded-md px-3 ${!isTrueFalse &&
                                q.options.length > 4
                                ? "pr-10"
                                : ""
                                } outline-none text-sm ${quizErr.options?.[optIndex]
                                  ? "border-red-600"
                                  : "border-gray-300"
                                } ${isTrueFalse
                                  ? "bg-gray-100"
                                  : ""
                                }`}
                            />

                            {/* Show delete icon on ALL options only when options are more than 4 */}
                            {!isTrueFalse && q.options.length > 4 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteQuizOption(
                                    qIndex,
                                    optIndex
                                  )
                                }
                                className="
              absolute
              right-2
              top-1/2
              -translate-y-1/2
              flex
              items-center
              justify-center
              bg-transparent
              border-none
              cursor-pointer
              p-0
              z-10
            "
                              >
                                <img
                                  src={Delete}
                                  alt="Delete"
                                  className="w-[16px] h-[16px] object-contain"
                                />
                              </button>
                            )}
                          </div>

                          {quizErr.options?.[optIndex] && (
                            <p className="text-red-600 text-xs mt-1">
                              {quizErr.options[optIndex]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* =================================================
                        CORRECT ANSWER
                    ================================================= */}

                    <p className="text-sm font-semibold mt-4 mb-2">
                      Correct Answer{" "}
                      <span className="text-red-600">
                        *
                      </span>
                    </p>

                    {/* MULTIPLE SELECT */}
                    {isMultiple && (
                      <div className="flex flex-wrap items-center gap-8">
                        {q.options.map(
                          (
                            opt,
                            optIndex
                          ) => (
                            <label
                              key={optIndex}
                              className="
                flex
                items-center
                gap-2
                cursor-pointer
              "
                            >
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(
                                    q.correctAnswers
                                  ) &&
                                  q.correctAnswers.includes(
                                    optIndex
                                  )
                                }
                                onChange={() => {
                                  toggleCorrectAnswer(
                                    qIndex,
                                    optIndex
                                  );

                                  clearQuizError(
                                    qIndex,
                                    "correctAnswers"
                                  );
                                }}
                                className="
                  w-4
                  h-4
                  accent-[#D50000]
                "
                              />

                              <span className="text-[15px] text-[#3A3A3A]">
                                {`Option ${optIndex + 1}`}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    )}

                    {/* SINGLE SELECT / TRUE-FALSE */}
                    {!isMultiple && (
                      <div className="flex flex-wrap items-center gap-8">
                        {q.options.map(
                          (
                            opt,
                            optIndex
                          ) => (
                            <label
                              key={optIndex}
                              className="
                flex
                items-center
                gap-2
                cursor-pointer
              "
                            >
                              <input
                                type="radio"
                                name={`correctAnswer-${q.id}`}
                                checked={
                                  q.correctAnswer ===
                                  optIndex
                                }
                                onChange={() => {
                                  updateQuiz(
                                    qIndex,
                                    "correctAnswer",
                                    optIndex
                                  );

                                  clearQuizError(
                                    qIndex,
                                    "correctAnswer"
                                  );
                                }}
                                className="
                  w-4
                  h-4
                  accent-[#D50000]
                "
                              />

                              <span className="text-[15px] text-[#3A3A3A]">
                                {isTrueFalse
                                  ? opt
                                  : `Option ${optIndex + 1}`}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    )}

                    {/* SINGLE / TRUE-FALSE ERROR */}
                    {!isMultiple &&
                      quizErr.correctAnswer && (
                        <p className="text-red-600 text-xs mt-2">
                          {quizErr.correctAnswer}
                        </p>
                      )}

                    {/* MULTIPLE ERROR */}
                    {isMultiple &&
                      quizErr.correctAnswers && (
                        <p className="text-red-600 text-xs mt-2">
                          {quizErr.correctAnswers}
                        </p>
                      )}
                  </>
                )}
              </div>
            );
          }
        )}

        {/* =====================================================
            WEIGHTAGE
        ===================================================== */}

        <div className="mt-5 bg-white rounded-2xl shadow-sm p-5">
          <h3 className="text-base font-bold text-[#1B1B1B] mb-4">
            Weightage (Points)
          </h3>

          <div className="max-w-sm">
            <label className="block text-sm font-medium mb-1">
              Weightage <span className="text-red-600">*</span>
            </label>

            <input
              type="number"
              min="1"
              placeholder="Enter Weightage"
              value={weightage}
              onWheel={(e) => e.currentTarget.blur()}
              onChange={(e) => {
                setWeightage(e.target.value);
                clearError("weightage");
              }}
              className={`w-full h-9 border rounded-md px-3 outline-none text-sm ${errors.weightage
                ? "border-red-600"
                : "border-gray-300"
                }`}
            />

            {errors.weightage && (
              <p className="text-red-600 text-xs mt-1">
                {errors.weightage}
              </p>
            )}
          </div>
        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <div className="flex justify-between items-center mt-6">
          <button
            type="button"
            onClick={
              handleAddQuiz
            }
            className="text-red-700 font-semibold border border-red-700 rounded-md px-5 py-2 text-sm hover:bg-red-50"
          >
            Add Quiz
          </button>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() =>
                navigate(-1)
              }
              className="px-8 py-2 rounded-md border border-gray-300 bg-white text-gray-700 font-medium text-sm"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={
                handleSave
              }
              className="px-8 py-2 rounded-md bg-[#D50000] text-white font-medium text-sm"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncAddStar;